import { AssetType } from '../api/models/Shared.types';
import { fullUrlWithRecipe, Recipes } from '../Business/utility/ImageRecipeUtil';
import { Log, Utils } from '@lightningjs/sdk';
import { getDayInfoString, getTime } from '../Business/utility/DateFormatter';
import { DetailDescriptionDataSource } from '../Business/detail/DetailDescriptionDataSource';
import { StreamResolutions } from '../Business/utility/StreamResolutionUtil';
import { FavoriteRequestType } from '../Business/cms/FavoriteRibbonRequest';
import { Theme } from '../config/theme';
import { GuideTranslationKeys } from '../api/models/TranslationKeys';
import { IconDef, IDetailDescription } from '../Business/detail/detail.types';
import { IRibbon2Detail, IRibbon2Schedule, IScheduleTime } from '../api/models/AndromeTV.types';
import { Backend } from '../api/Backend';
import { CardInfoIcon } from '../Business/cms/CardInfo';
import { getAgeratingIcon } from '../Business/detail/DetailDSUtil';

export interface MiniDetailConversionData extends IRibbon2Detail {
  schedule: IRibbon2Schedule;
  recordingIcons?: CardInfoIcon[];
  favoriteIcon?: string;
}

export interface MiniDetailData {
  description: IDetailDescription;
  image: string | null;
}

export function convertDataForMiniDetail(data: MiniDetailConversionData, backend: Backend): MiniDetailData | undefined {
  // console.log(`ConvertData for type: ${data.type}`, data);
  let result: MiniDetailData | undefined;
  switch (data.type) {
    case AssetType.VOD:
      result = convertVOD(data, backend);
      break;
    case AssetType.SCHEDULE:
      result = convertSCHEDULE(data, backend);
      break;
    case AssetType.CHANNEL:
      result = convertChannel(data, backend);
      break;
    case AssetType.PROGRAM:
      result = convertProgram(data, backend);
      break;
    case AssetType.ARTICLE:
      result = convertArticle(data, backend);
      break;
    case AssetType.SERIES:
      result = convertProgram(data, backend);
      break;
    case AssetType.PAGE:
      result = convertPage(data, backend);
      break;
    case AssetType.BUNDLE:
      result = convertVOD(data, backend);
      break;
    default:
      Log.warn('MiniDetailUtils: unsupported type:', data.type, data);
      result = convertProgram(data, backend);
  }
  // hide title, synopsis and image when program is adult
  if (result && data.adult) {
    result.description = {
      title: backend.translations.forKey(GuideTranslationKeys.guide_adult_title),
      synopsis: '',
    };
    result.image = Utils.asset('/images/adult.png');
  }
  return result;
}

function convertArticle(data: MiniDetailConversionData, backend: Backend): MiniDetailData {
  let img;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    description: {
      title: data.title,
      synopsis: undefined,
      recordingIcons: undefined,
      metaInformation: undefined,
    },
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertPage(data: MiniDetailConversionData, backend: Backend): MiniDetailData {
  let img: string | undefined;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    description: {
      title: data.title,
      synopsis: data.synopsis,
      recordingIcons: undefined,
      metaInformation: undefined,
    },
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertProgram(data: MiniDetailConversionData, backend: Backend): MiniDetailData {
  const dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  let metaData: string;
  if (data.schedule) {
    metaData = getMetaData(backend, data, data.schedule.published, data.season, data.episode, data.genres);
  } else {
    metaData = getMetaData(backend, data, undefined, data.season, data.episode, data.genres);
  }
  let img: string | undefined;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    description: {
      title: data.title,
      synopsis: data.synopsis,
      recordingIcons: getIcons({ age: data.minimumAge, resolution: data.resolution }, dataHelper, data),
      metaInformation: metaData,
    },
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertChannel(data: MiniDetailConversionData, backend: Backend): MiniDetailData | undefined {
  if (data.subType === FavoriteRequestType.CHANNEL_LOGO || data.subType === FavoriteRequestType.CHANNEL_TOGGLE) {
    return undefined;
  }
  let img: string | undefined;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    description: {
      title: data.title,
      synopsis: undefined,
      recordingIcons: undefined,
      metaInformation: undefined,
    },
    image: getImageUrl(img, backend, Recipes.LandscapeCard.Large),
  };
}

function convertVOD(data: MiniDetailConversionData, backend: Backend): MiniDetailData {
  const dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  let img: string | undefined;
  if (data.detailImage) {
    img = data.detailImage;
  } else {
    img = data.image;
  }
  return {
    description: {
      title: data.title,
      synopsis: data.synopsis,
      recordingIcons: getIcons({ age: data.minimumAge, resolution: data.resolution }, dataHelper, data),
      metaInformation: getMetaInfoVOD(data.productionYear, data.duration, data.genres, backend),
    },
    image: getImageUrl(img, backend, Recipes.DetailPosterRecipe),
  };
}

function convertSCHEDULE(data: MiniDetailConversionData, backend: Backend): MiniDetailData {
  const dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  return {
    description: {
      title: data.title,
      synopsis: data.synopsis,
      recordingIcons: getIcons({ age: data.minimumAge, resolution: data.resolution }, dataHelper, data),
      metaInformation: getMetaData(backend, data, data.schedule.published, data.season, data.episode, data.genres),
    },
    image: getImageUrl(data.image, backend, Recipes.DetailPosterRecipe),
  };
}

/**
 * Other functions
 * **/

function getMetaInfoVOD(
  year: number | undefined,
  duration: number | undefined,
  genres: string[] = [],
  backend: Backend
) {
  let yearStr = '';
  if (year) {
    yearStr = `${year}`;
  }
  let durStr = '';
  if (duration) {
    const dur = Math.floor(duration / 60);
    durStr = `  •  ${dur} min.`;
  }
  const genreStr = getGenres(genres, backend);

  let returnStr = `${yearStr}${durStr}${genreStr}`;
  if (returnStr.length > 1) {
    if (returnStr.startsWith('  •  ')) {
      returnStr = returnStr.substring(5);
    }
    return returnStr;
  } else {
    return undefined;
  }
}

/**\
 * General Functions
 * **/
function getIcons(
  icons: { resolution?: string; age?: number },
  dataHelper: DetailDescriptionDataSource,
  data: MiniDetailConversionData
) {
  let resIcon = undefined;
  switch (icons.resolution) {
    case StreamResolutions.SD:
      resIcon = dataHelper.getResolutionIcon(StreamResolutions.SD);
      break;
    case StreamResolutions.HD:
      resIcon = dataHelper.getResolutionIcon(StreamResolutions.HD);
      break;
    case StreamResolutions.UHD_FOURK || StreamResolutions.FOURK:
      resIcon = dataHelper.getResolutionIcon(StreamResolutions.UHD_FOURK);
      break;
  }
  // place to add other icons.
  // icons that need to be colored
  const iconArr: IconDef[] = [];
  const ageIcon = getAgeratingIcon(icons.age);
  if (ageIcon) {
    iconArr.push({ src: ageIcon, color: Theme.Colors.Primary });
  }
  if (resIcon) {
    iconArr.push({ src: resIcon, color: Theme.Colors.Primary });
  }

  // icons that don't need to be colored
  if (data.recordingIcons) {
    data.recordingIcons.forEach(iconObj => {
      if (iconObj.icon) {
        iconArr.push({ src: iconObj.icon, color: undefined });
      }
    });
  }

  if (data.favoriteIcon) {
    iconArr.push({ src: data.favoriteIcon, color: Theme.Colors.Primary });
  }

  // console.log('Icon arr', iconArr);
  return iconArr;
}

function getMetaData(
  backend: Backend,
  data: MiniDetailConversionData,
  schedule: IScheduleTime | undefined,
  season: number | undefined,
  episode: number | undefined,
  genres: string[] | undefined
) {
  const genresStr = getGenres(genres, backend);
  let dateStr = '';
  if (schedule) {
    dateStr = getFullTime(+schedule.start, +schedule.end, backend);
  }

  const dataHelper = new DetailDescriptionDataSource(data.id, data.type, backend);
  let seasonStr = dataHelper.getFormattedSeasonInfo(season, episode);
  if (!seasonStr) {
    seasonStr = '';
  }
  if (seasonStr.length > 1) {
    seasonStr = '  •  ' + seasonStr;
  }
  let returnStr = `${dateStr}${seasonStr}${genresStr}`;
  if (returnStr.startsWith('  •  ')) {
    returnStr = returnStr.substring(5);
  }
  return returnStr;
}

function getImageUrl(url = '', backend: Backend, recipe: string) {
  if (!url) return null;

  return fullUrlWithRecipe(backend, url, recipe);
}

function getGenres(genres: string[] = [], backend: Backend) {
  const genresArray = backend.genres.forGenres(genres);
  let genreStr = '';
  let first = true;
  for (let i = 0; i < genresArray.length; i++) {
    if (first) {
      genreStr += `  •  ${genresArray[i]}`;
      first = false;
    } else {
      genreStr += `  •  ${genresArray[i]}`;
    }
  }
  return genreStr;
}

function getFullTime(start: number, end: number, backend: Backend) {
  if (!start || !end) return '';
  const day = getDayInfoString(new Date(start * 1000), false, backend);
  let dateStr = '';

  const first = new Date(start * 1000);
  const second = new Date(end * 1000);

  const time = getTime(first);
  const time2 = getTime(second);
  dateStr += `${day}, ${time} - ${time2}`;
  return dateStr;
}
